import { useState, useEffect, useRef } from 'react'
import { __ } from '@wordpress/i18n'
import SbUtils from '../../Utils/SbUtils'
import Button from './Button';
import Checkbox from './Checkbox';
import { TagIcon, FolderIcon } from './Icons/WooIcons';

const Source = ( props ) => {
    const [ showPopover, setShowPopover ] = useState( false );
    const popoverRef = useRef( null );
    const triggerRef = useRef( null );

    // Parse source info for address display (original behavior for non-WooCommerce sources)
    const info = SbUtils.jsonParse(props?.info) ?? null;

    // Check if this is a WooCommerce or EDD source - always show products section for these
    const isWooCommerce = props.provider === 'woocommerce';
    const isEdd = props.provider === 'edd';
    const isLocalProductSource = isWooCommerce || isEdd;

    // Check if there's content to show in the popover (products, categories, or tags)
    const hasProductsInfo = props?.productsInfo && Array.isArray( props.productsInfo ) && props.productsInfo.length > 0;
    const hasPopoverContent = hasProductsInfo ||
        (props?.categoriesInfo && Array.isArray( props.categoriesInfo ) && props.categoriesInfo.length > 0) ||
        (props?.tagsInfo && Array.isArray( props.tagsInfo ) && props.tagsInfo.length > 0);

    // Close popover when clicking outside
    useEffect( () => {
        if ( !showPopover ) return;

        const handleClickOutside = ( event ) => {
            // Check if click is outside popover and trigger
            const isOutsidePopover = popoverRef.current && !popoverRef.current.contains( event.target );
            const isOutsideTrigger = triggerRef.current && !triggerRef.current.contains( event.target );

            if ( isOutsidePopover && isOutsideTrigger ) {
                setShowPopover( false );
            }
        };

        document.addEventListener( 'mousedown', handleClickOutside );
        return () => document.removeEventListener( 'mousedown', handleClickOutside );
    }, [ showPopover ] );

    // Inline SVG for outlined info icon (mdi:information-outline)
    const InfoOutlineIcon = () => (
        <svg className='sb-source-info-icon' width='14' height='14' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path d='M11 9H13V7H11V9ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM11 17H13V11H11V17Z' fill='currentColor'/>
        </svg>
    );

    // Inline SVG for pencil/edit icon
    const PencilIcon = () => (
        <svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path d='M1.5 8.625V10.5H3.375L8.905 4.97L7.03 3.095L1.5 8.625ZM10.355 3.52C10.55 3.325 10.55 3.01 10.355 2.815L9.185 1.645C8.99 1.45 8.675 1.45 8.48 1.645L7.565 2.56L9.44 4.435L10.355 3.52Z' fill='currentColor'/>
        </svg>
    );

    // Parse categories and tags info from props
    const hasCategoriesInfo = props?.categoriesInfo && Array.isArray( props.categoriesInfo ) && props.categoriesInfo.length > 0;
    const hasTagsInfo = props?.tagsInfo && Array.isArray( props.tagsInfo ) && props.tagsInfo.length > 0;

    return (
        <div
            className={ 'sb-source-item ' + ( props?.customClass ? props?.customClass : '' ) + ( hasPopoverContent ? ' sb-source-has-popover' : '' ) }
            data-checked={props.isChecked}
            onClick={ ( event ) => {
                event.stopPropagation()
                event.preventDefault()
                if( props?.onClick )
                    props.onClick()
            }}
        >
            <div className='sb-source-item-top sb-fs'>
                {
                    ( props?.checkbox || props?.radiobox ) &&
                    <div className='sb-source-item-box'>
                        {
                            props.checkbox &&
                            <Checkbox
                                value={ props.isChecked }
                                enabled={ true }
                                onChange={ () => {
                                    if( props?.onClick )
                                        props.onClick()
                                }}
                            />
                        }
                        {
                            props.radiobox &&
                            <Checkbox
                                value={ props.isChecked }
                                enabled={ true }
                                onChange={ () => {
                                    if( props?.onClick )
                                        props.onClick()
                                }}
                            />
                        }
                    </div>
                }
                <div className='sb-source-item-icon'>
                    { SbUtils.printIcon( props.provider + '-provider', 'sb-source-svg' ) }
                </div>
                <div className='sb-source-item-info'>
                    <span className='sb-text-small sb-bold'>{ SbUtils.decodeHtmlEntities( props.name ) }</span>
                    <span className='sb-dark2-text sb-text-tiny'>
                        { isLocalProductSource ? (
                            <span
                                ref={triggerRef}
                                className='sb-source-products-trigger'
                                onClick={ ( event ) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    setShowPopover( !showPopover );
                                }}
                            >
                                { props.badge || ( isWooCommerce ? __( 'WooCommerce Source', 'sb-customizer' ) : __( 'EDD Source', 'sb-customizer' ) ) }
                                <InfoOutlineIcon />
                            </span>
                        ) : (
                            <>
                                { info?.address ? SbUtils.decodeHtmlEntities( info.address ) : props.provider + ' ' + __('Reviews', 'sb-customizer') }
                            </>
                        ) }
                    </span>
                </div>
                {
                    ( props?.removeIcon || props?.infoIcon || (props?.needHistoryCheck && props?.needHistoryCheck === true) ) &&
                        <div className='sb-source-act-ctn'>
                            {
                                props?.needHistoryCheck && props?.needHistoryCheck === true &&
                                <div className='sb-source-item-needhist-icon sb-source-act-icon-ctn'>
                                    <div
                                        className='sb-source-act-icon sb-tr-2'
                                    >
                                        {
                                            SbUtils.printTooltip(
                                            __('Unable to retrieve reviews history for this source. Clear the cache or reconnect the source to try again.', 'sb-customizer'),
                                            {
                                                type : "white",
                                                position : 'top-center',
                                                replaceText : false
                                            } )
                                        }
                                        { SbUtils.printIcon( 'warning', false, false, 16  ) }
                                    </div>
                                </div>
                            }
                            {
                                props?.removeIcon &&
                                <div className={ 'sb-source-item-remove-icon sb-source-act-icon-ctn' + ( props?.removeDisabled ? ' sb-source-remove-disabled' : '' ) }>
                                    <div
                                        className='sb-source-act-icon sb-tr-2'
                                        onClick={ () => {
                                            if( props?.removeDisabled ) return;
                                            if( props?.onRemoveClick )
                                                props.onRemoveClick()
                                        }}
                                    >
                                        { SbUtils.printIcon( 'trash', false, false, 11  ) }
                                    </div>
                                </div>
                            }
                            {
                                props?.infoIcon &&
                                <div className='sb-source-item-info-icon sb-source-act-icon-ctn'>
                                    <div
                                        className='sb-source-act-icon sb-tr-2'
                                        onClick={ () => {
                                            props.onShowInfoClick()
                                        }}
                                    >
                                        { SbUtils.printIcon( 'cog', false, false, 15 ) }
                                    </div>
                                </div>
                            }
                        </div>
                }
            </div>
            {
                ( props?.infoIcon && props?.isCollapsed === true  ) &&
                <div className='sb-source-dp-info sb-fs'>
                    <div className='sb-source-dp-info-item'>
                        <strong>ID</strong>
                        <span>{ props.accountId }</span>
                        <Button
                            icon='copy'
                            type='secondary'
                            size='small'
                            boxshadow={false}
                            onClick={ () => {
                                SbUtils.copyToClipBoard( props.accountId, props.editorNotification )
                            } }
                        />
                    </div>
                </div>
            }
            {
                ( isLocalProductSource || hasPopoverContent ) && showPopover &&
                <div ref={popoverRef} className='sb-source-products-popover'>
                    <div className='sb-source-products-popover-header'>
                        <span className='sb-text-small sb-bold'>
                            { props?.badge || __( 'Selected Items', 'sb-customizer' ) }
                        </span>
                        {
                            props?.onEditClick &&
                            <button
                                className='sb-source-products-edit-link'
                                onClick={ ( event ) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    props.onEditClick();
                                }}
                            >
                                <PencilIcon />
                                { __( 'Edit', 'sb-customizer' ) }
                            </button>
                        }
                    </div>
                    <div className='sb-source-products-list'>
                        {/* Categories */}
                        {
                            hasCategoriesInfo && props.categoriesInfo.map( ( category, index ) => {
                                // Support both WooCommerce (product_count) and EDD (download_count)
                                const itemCount = category.product_count ?? category.download_count ?? 0;
                                const itemLabel = isEdd
                                    ? ( itemCount === 1 ? __( 'Download', 'sb-customizer' ) : __( 'Downloads', 'sb-customizer' ) )
                                    : ( itemCount === 1 ? __( 'Product', 'sb-customizer' ) : __( 'Products', 'sb-customizer' ) );
                                return (
                                    <div key={ `cat-${ index }` } className='sb-source-product-item sb-source-category-item'>
                                        <div className='sb-source-item-details'>
                                            <span className='sb-source-product-name'>{ category.name }</span>
                                            <span className='sb-source-product-meta'>
                                                { __( 'Category', 'sb-customizer' ) } • { itemCount } { itemLabel }
                                            </span>
                                        </div>
                                        <div className='sb-source-item-type-icon'>
                                            <FolderIcon />
                                        </div>
                                    </div>
                                );
                            })
                        }
                        {/* Tags */}
                        {
                            hasTagsInfo && props.tagsInfo.map( ( tag, index ) => {
                                // Support both WooCommerce (product_count) and EDD (download_count)
                                const itemCount = tag.product_count ?? tag.download_count ?? 0;
                                const itemLabel = isEdd
                                    ? ( itemCount === 1 ? __( 'Download', 'sb-customizer' ) : __( 'Downloads', 'sb-customizer' ) )
                                    : ( itemCount === 1 ? __( 'Product', 'sb-customizer' ) : __( 'Products', 'sb-customizer' ) );
                                return (
                                    <div key={ `tag-${ index }` } className='sb-source-product-item sb-source-tag-item'>
                                        <div className='sb-source-item-details'>
                                            <span className='sb-source-product-name'>{ tag.name }</span>
                                            <span className='sb-source-product-meta'>
                                                { __( 'Tag', 'sb-customizer' ) } • { itemCount } { itemLabel }
                                            </span>
                                        </div>
                                        <div className='sb-source-item-type-icon'>
                                            <TagIcon />
                                        </div>
                                    </div>
                                );
                            })
                        }
                        {/* Individual Products Only */}
                        {
                            props.productsInfo && props.productsInfo.map( ( product, index ) => (
                                <div key={ `prod-${ index }` } className='sb-source-product-item sb-source-product-only'>
                                    <span className='sb-source-product-name'>{ product.name }</span>
                                </div>
                            ))
                        }
                    </div>
                </div>
            }
        </div>
    )
}

export default Source;